import { useState, useCallback } from 'react';
import axios from 'axios';
import appConstants from '../constants/appConstants';

const getAuthToken = () => localStorage.getItem('authToken');

export const useFeeManagement = (studentId, academicYearId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fee Structure related functions
  const createFeeStructure = useCallback(
    async (feeStructureData) => {
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${appConstants.API_ROOT_URL}create-fee-structure`,
          {
            student_id: studentId,
            academic_year_id: academicYearId,
            ...feeStructureData,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        setLoading(false);
        return response.data;
      } catch (err) {
        setError(
          err.response?.data?.message || 'Failed to create fee structure',
        );
        setLoading(false);
        throw err;
      }
    },
    [studentId, academicYearId],
  );

  const getFeeStructure = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-fee-structure`,
        {
          student_id: studentId,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch fee structure');
      setLoading(false);
      throw err;
    }
  }, [studentId, academicYearId]);

  // Installment related functions
  const createInstallments = useCallback(
    async (installmentData) => {
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${appConstants.API_ROOT_URL}create-installments`,
          {
            student_id: studentId,
            academic_year_id: academicYearId,
            ...installmentData,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        setLoading(false);
        return response.data;
      } catch (err) {
        setError(
          err.response?.data?.message || 'Failed to create installments',
        );
        setLoading(false);
        throw err;
      }
    },
    [studentId, academicYearId],
  );

  const getInstallments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-installments`,
        {
          student_id: studentId,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch installments');
      setLoading(false);
      throw err;
    }
  }, [studentId, academicYearId]);

  const updateInstallmentStatus = useCallback(
    async (installmentId, status, remarks = '') => {
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${appConstants.API_ROOT_URL}update-installment-status`,
          {
            installment_id: installmentId,
            status,
            remarks,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        setLoading(false);
        return response.data;
      } catch (err) {
        setError(
          err.response?.data?.message || 'Failed to update installment status',
        );
        setLoading(false);
        throw err;
      }
    },
    [],
  );

  // Payment/Receipt related functions
  const recordPayment = useCallback(
    async (paymentData) => {
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${appConstants.API_ROOT_URL}record-payment`,
          {
            student_id: studentId,
            academic_year_id: academicYearId,
            ...paymentData,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        setLoading(false);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to record payment');
        setLoading(false);
        throw err;
      }
    },
    [studentId, academicYearId],
  );

  const getPaymentHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-payment-history`,
        {
          student_id: studentId,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to fetch payment history',
      );
      setLoading(false);
      throw err;
    }
  }, [studentId, academicYearId]);

  const generateReceipt = useCallback(async (paymentId) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}generate-receipt`,
        {
          payment_id: paymentId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate receipt');
      setLoading(false);
      throw err;
    }
  }, []);

  // Summary and calculation functions
  const getStudentFeeSheet = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_student_fee_sheet?student_id=${studentId}&academic_year_id=${academicYearId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to fetch student fee sheet',
      );
      setLoading(false);
      throw err;
    }
  }, [studentId, academicYearId]);

  const getFeesSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-fees-summary`,
        {
          student_id: studentId,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch fees summary');
      setLoading(false);
      throw err;
    }
  }, [studentId, academicYearId]);

  // Fee assignment function
  const assignStudentFee = useCallback(
    async (batchId, joinDate) => {
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const response = await axios.post(
          `${appConstants.API_ROOT_URL}assign_student_fee`,
          {
            student_id: studentId,
            batch_id: batchId,
            academic_year_id: academicYearId,
            join_date: joinDate,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        setLoading(false);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to assign student fee');
        setLoading(false);
        throw err;
      }
    },
    [studentId, academicYearId],
  );

  const undoAssignStudentFee = useCallback(async (studentFeeAssignmentId) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}undo-assign-student-fee`,
        {
          student_fee_assignment_id: studentFeeAssignmentId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to undo student fee assignment',
      );
      setLoading(false);
      throw err;
    }
  }, []);

  // Make installment due function
  const makeInstallmentDue = useCallback(async (installmentId, date) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}make_installment_due`,
        {
          student_fee_installment_id: installmentId,
          date: date,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to make installment due');
      setLoading(false);
      throw err;
    }
  }, []);

  return {
    loading,
    error,
    // Fee Structure
    createFeeStructure,
    getFeeStructure,
    // Installments
    createInstallments,
    getInstallments,
    updateInstallmentStatus,
    // Payments
    recordPayment,
    getPaymentHistory,
    generateReceipt,
    // Summary
    getStudentFeeSheet,
    getFeesSummary,
    // New functions
    assignStudentFee,
    undoAssignStudentFee,
    makeInstallmentDue,
  };
};
